/**
 * SSR-safe lazy loading utility for React components.
 *
 * Supports both **named exports** (pass `exportName` as the 2nd arg) and **default exports**
 * (omit `exportName`). Works correctly with @loadable/babel-plugin for server-side rendering
 * by exporting the function as `loadable`, which the babel plugin recognizes through
 * name-based pattern matching on the local identifier.
 *
 * Adds silent retry on chunk-load failure (2 attempts, 1s/4s backoff) to recover from
 * transient mobile-network errors — implemented in `@shared/core/chunkRetry`. See
 * doc/plans/PRNT-4319-chunk-load-retry.md for the original analysis and
 * doc/plans/chunk-retry-backoff.md for the current retry behavior.
 *
 * @module @shared/core/loadableFromNamedExport
 */

import type { DefaultComponent, LoadableComponent, LoadableLibrary } from '@loadable/component';
import loadableOriginal from '@loadable/component';
import type { ComponentProps, ComponentType } from 'react';
import type React from 'react';

import { delay, isChunkLoadError, RETRY_DELAYS_MS, withChunkRetry, withQueryParam } from '@shared/core/chunkRetry';
import globalWindow from '@shared/core/globals';
import i18n from '@shared/core/i18n/i18n';
import { I18N_FALLBACK_LOCALE } from '@shared/core/i18n/i18n.constants';
import type { PreloadFailure } from '@shared/core/i18n/switchLanguage';
import { findMissingBundles, getEffectiveLanguage, isBundleUsable, listNamespaces, loadBundles, loadForLanguage } from '@shared/core/i18n/switchLanguage';
import { addAction } from '@shared/utils/logger';

// Extracts the original props type from React components without the implicit children prop.
// React.FC<Props> automatically adds `children?: ReactNode` to the props type, but we want
// to preserve the exact props interface as defined by the component author.
// This ensures LoadableComponent<Props> matches the original component's props signature.
// - First checks React.FC<P> and extracts P (the original props without children)
// - Then checks React.ComponentType<P> and extracts P (covers other component patterns)
// - Falls back to ComponentProps<T> for intrinsic elements (div, span, etc.)
// - Returns never for non-component types (should not occur in practice)
type ExtractComponentProps<T> =
  T extends React.FC<infer P>
    ? P
    : T extends React.ComponentType<infer P>
      ? P
      : T extends keyof JSX.IntrinsicElements | React.JSXElementConstructor<any> // eslint-disable-line @typescript-eslint/no-explicit-any
        ? ComponentProps<T>
        : never;

// `@loadable/babel-plugin` rewrites `() => import('./X')` into a config object
// { requireAsync, requireSync, chunkName, resolved } in production builds. Handle both shapes.
type BabelLoadableConfig = {
  requireAsync: (...args: unknown[]) => Promise<unknown>;
  chunkName?: () => string;
  [key: string]: unknown;
};

const wrapLoaderArg = (arg: unknown): unknown => {
  if (typeof arg === 'function') {
    return withChunkRetry(arg as () => Promise<unknown>);
  }
  if (arg && typeof arg === 'object' && typeof (arg as BabelLoadableConfig).requireAsync === 'function') {
    const config = arg as BabelLoadableConfig;
    const chunkName = typeof config.chunkName === 'function' ? config.chunkName() : undefined;
    const originalRequireAsync = config.requireAsync;
    // The babel-plugin's `requireAsync` is a method that calls `this.resolve(...)` and
    // friends — wrap it as a `function` (not an arrow) so we can preserve `this` from the
    // call site (which is the config object loadable hands back to us) and forward args.
    const wrappedRequireAsync = function (this: unknown, ...args: unknown[]) {
      // The inner arrow inherits `this` lexically from this `function`, so `apply(this, …)`
      // forwards the caller's `this` (the config object loadable hands back) to the original.
      return withChunkRetry(() => originalRequireAsync.apply(this, args), chunkName)();
    };
    return { ...config, requireAsync: wrappedRequireAsync };
  }
  return arg;
};

/**
 * Loads a chunk's i18n namespaces together with the chunk, so the loadable resolves only once BOTH
 * are in place. Every `useTranslation()` inside the chunk then finds its bundle in the i18n store and
 * never suspends — which matters because the app runs legacy-mode Suspense (React 17
 * `ReactDOM.render`): a component that suspends inside an already-visible boundary hides that whole
 * boundary with `display:none`. In admin that boundary is usually the root one, so one late
 * namespace fetch blanks the entire page.
 *
 * WHICH namespaces: `window.__I18N_CHUNK_NAMESPACES__[chunkName]` — the build's attribution of chunk
 * group → namespaces from bundler stats (config/translationsManifestPlugin/chunkNamespaces.ts),
 * installed by the i18n store script on SSR pages and by an inline tag on static HTML. Exact, covers
 * every chunk, no per-lazy code. Read at LOAD time, not definition time: the map is set by a
 * parser-blocking script, but a loadable may be defined by a module that evaluates before an inline
 * tag placed later in the document.
 *
 * Applies to the `@loadable/babel-plugin` config shape only (`requireAsync` + `chunkName`), which is
 * what every production loadable is; `.load()` / `.preload()` go through `requireAsync` too, so
 * hover-prefetch fetches strings with the chunk. Untransformed thunks pass through unchanged (dev
 * builds, which emit no map either).
 *
 * Known gap: if the chunk's modules are ALREADY installed in webpack when the component first mounts
 * (`isReady` → `requireSync`), @loadable/component renders synchronously and skips `requireAsync`.
 * That only happens after a previous async load of the same chunk — which loaded the namespaces —
 * so in practice the store is already warm.
 */
const withNamespacePreload = (arg: unknown): unknown => {
  if (!(arg && typeof arg === 'object' && typeof (arg as BabelLoadableConfig).requireAsync === 'function')) {
    return arg;
  }
  const config = arg as BabelLoadableConfig;
  const chunkName = typeof config.chunkName === 'function' ? config.chunkName() : undefined;
  const originalRequireAsync = config.requireAsync;
  const requireAsync = function (this: unknown, ...args: unknown[]) {
    const load = () => originalRequireAsync.apply(this, args);
    const namespaces = chunkName ? globalWindow.__I18N_CHUNK_NAMESPACES__?.[chunkName] : undefined;
    if (!namespaces || namespaces.length === 0) {
      return load();
    }
    rememberPreloaded(namespaces);
    const startLanguage = i18n.language;
    return Promise.all([load(), loadNamespacesForChunk(namespaces, chunkName)]).then(async ([loadedModule]) => {
      // A language switch that began while we loaded left these bundles in the OLD language;
      // mounting now would suspend with no boundary, so fetch the new one first.
      const targetLanguage = getEffectiveLanguage();
      if (targetLanguage && targetLanguage !== startLanguage) {
        try {
          await loadForLanguage(targetLanguage, namespaces);
        } catch {
          // loadForLanguage never rejects and reports its own failures; the module must still resolve.
        }
        // The switch may have landed meanwhile: check the language the component will mount in.
        if (i18n.language !== startLanguage) await loadNamespacesForChunk(namespaces, chunkName);
      }
      return loadedModule;
    });
  };
  return { ...config, requireAsync };
};

/**
 * Loads a chunk's namespaces in the page's language and checks they landed. `i18n.loadNamespaces`
 * resolves even when a bundle failed, and a component whose bundle is missing suspends with no
 * `<Suspense>` above it. So a missing bundle is fetched again with the chunk retry's backoff
 * (1 s, then 4 s). Every load is capped at 15 s, so the worst case is 15 + 1 + 15 + 4 + 15 = 50 s.
 * The check is synchronous, so a chunk whose bundles loaded pays nothing. The page language is
 * read on every attempt: a language switch mid-retry is checked in the language it lands in.
 *
 * Still missing after the retries: reported as `ChunkNamespacePreloadFailed` (`rejected: false`
 * means degraded but rendering). The chunk then rejects if Joy's `useTranslation` would have
 * nothing to walk (no bundle in the page language nor in en-US): the component would otherwise
 * suspend mid-render into the same error boundary, so rejecting shows that boundary's reload UI
 * up front, with a clear error. With en-US present it resolves: the hook renders from en-US.
 */
const loadNamespacesForChunk = async (namespaces: ReadonlyArray<string>, chunkName: string | undefined): Promise<void> => {
  const missingNow = () => (i18n.language ? findMissingBundles(i18n.language, namespaces) : []);
  // `loadNamespaces` also registers the namespaces in `options.ns`; `loadBundles` joins the same
  // requests and adds the deadline (marking a stalled one failed ends `loadNamespaces` too).
  const [, initialFailure] = await Promise.all([i18n.loadNamespaces(namespaces).catch(() => undefined), i18n.language ? loadBundles(i18n.language, namespaces) : undefined]);
  let failure: PreloadFailure | undefined = initialFailure;
  let missing = missingNow();
  for (const backoffMs of RETRY_DELAYS_MS) {
    if (missing.length === 0) return;
    await delay(backoffMs);
    const lng = i18n.language;
    const retryFailure = lng ? await loadBundles(lng, findMissingBundles(lng, namespaces)) : undefined;
    failure = failure ?? retryFailure;
    missing = missingNow();
  }
  if (missing.length === 0) return;
  const unrenderable = missing.filter(ns => !isBundleUsable(I18N_FALLBACK_LOCALE, ns));
  addAction('ChunkNamespacePreloadFailed', {
    chunkName,
    lng: i18n.language,
    namespaces: listNamespaces(missing),
    missingCount: missing.length,
    reason: failure?.reason ?? 'missing',
    message: failure?.message ?? '',
    rejected: unrenderable.length > 0,
  });
  if (unrenderable.length > 0) {
    throw new Error(`i18n namespaces failed to load for chunk ${chunkName ?? '(unnamed)'} in ${i18n.language}: ${unrenderable.join(',')}`);
  }
};

/**
 * Every namespace preloaded for some chunk, so a later language switch can bring the new locale's
 * bundles for everything already on screen instead of letting each component suspend on its own.
 */
const preloadedNamespaces = new Set<string>();
let languageChangeListenerInstalled = false;
const rememberPreloaded = (namespaces: ReadonlyArray<string>): void => {
  namespaces.forEach(namespace => preloadedNamespaces.add(namespace));
  if (!languageChangeListenerInstalled && typeof i18n.on === 'function') {
    languageChangeListenerInstalled = true;
    i18n.on('languageChanged', () => {
      // Covers a raw `changeLanguage` (switchLanguage already loaded these); never rejects, reports its failures.
      if (preloadedNamespaces.size > 0 && i18n.language) {
        void loadForLanguage(i18n.language, [...preloadedNamespaces]);
      }
    });
  }
};

/**
 * Create a lazy-loaded React component.
 *
 * **For default exports** — omit `exportName`:
 * ```typescript
 * const MyComp = loadable(() => import('./MyComp'));
 * ```
 *
 * **For named exports** — pass the export name as the 2nd argument:
 * ```typescript
 * const MyComp = loadable(() => import('./MyComp'), 'MyComp');
 * ```
 *
 * SSR-safe because:
 * 1. Exported with the name `loadable`, which `@loadable/babel-plugin` recognizes
 *    through name-based pattern matching on the local identifier.
 * 2. Named-export path uses `resolveComponent` (not `{ default: ... }` wrapping)
 *    to avoid hoist-non-react-statics race conditions.
 *
 * Both code paths also load the chunk's i18n namespaces with the chunk (see `withNamespacePreload`
 * above), and both silently retry on chunk-load failure — up to 2 attempts with a 1s/4s
 * backoff, each re-fetching the chunk with a cache-busting query param, under a total
 * ~40s budget. See doc/plans/chunk-retry-backoff.md.
 *
 * **Do NOT rename the import** — the babel plugin requires the local identifier `loadable`:
 * ```typescript
 * import { loadable } from '@shared/core/loadableFromNamedExport'; // OK
 * import { loadable as loadableX } from '@shared/core/loadableFromNamedExport'; // BREAKS SSR
 * ```
 *
 * `loadable.lib()` for non-component dynamic imports is exposed as a property on this
 * same `loadable` export — it applies the same silent chunk-retry around the library
 * loader while keeping the `.lib` render-prop API. Import `loadable` from here and call
 * `loadable.lib(...)` rather than importing `@loadable/component` directly; the babel
 * plugin matches `<ident>.lib(...)` where `<ident>` is named `loadable`, so the callsite
 * keeps its SSR transform (`requireSync`/`resolve`/`chunkName`) — which
 * `@loadable/component` asserts on whenever a `ChunkExtractor` is in context, `ssr: false`
 * or not.
 *
 * @see https://loadable-components.com/docs/api-loadable-component/
 * @see https://github.com/gregberge/loadable-components/issues/420
 */
// Mirrors @loadable/component's `OptionsWithoutResolver<Props>`. `resolveComponent` is
// owned by the named-export overload (set internally from `exportName`), so it's not
// exposed here.
type LoadableOptions<Props = unknown> = Readonly<{
  fallback?: JSX.Element;
  ssr?: boolean;
  cacheKey?: (props: Props) => unknown;
}>;

// Overload A: default-export (omit `exportName`)
export function loadable<P>(importFn: () => Promise<{ default: ComponentType<P> }>, options?: LoadableOptions): LoadableComponent<P>;

// Overload B: named-export
export function loadable<TModule extends Record<string, unknown>, TExportName extends keyof TModule & string>(
  importFn: () => Promise<TModule>,
  exportName: TExportName,
  options?: LoadableOptions,
): LoadableComponent<ExtractComponentProps<TModule[TExportName]>>;

// Implementation — the public API surface is constrained by the overloads above; this body
// uses `unknown` at the input boundary and casts at the single point where we hand off to
// @loadable/component (which accepts either a thunk OR the babel-plugin-transformed config
// object as its first arg — a runtime contract not reflected in its TS types).
export function loadable(importFn: unknown, second?: string | LoadableOptions, third?: LoadableOptions): LoadableComponent<unknown> {
  const wrappedLoader = withNamespacePreload(wrapLoaderArg(importFn)) as () => Promise<unknown>;
  if (typeof second === 'string') {
    const exportName = second;
    const options = third;
    return loadableOriginal(wrappedLoader, {
      resolveComponent: (m: unknown) => (m as Record<string, ComponentType<unknown>>)[exportName],
      ...options,
    });
  }
  // The no-resolver overload of @loadable/component requires the loader to resolve to a
  // `DefaultComponent` (module with a default export). The runtime accepts the babel-plugin
  // config object too — cast to satisfy the stricter typed signature.
  return loadableOriginal(wrappedLoader as () => Promise<DefaultComponent<unknown>>, { ...second });
}

/**
 * Retry-wrapped `loadable.lib()` for non-component dynamic imports (filestack-js,
 * lottie-web, `@vimeo/player`). Same silent-retry + cache-bust behavior as `loadable()`:
 * `wrapLoaderArg` handles both the untransformed thunk and the babel-plugin config-object
 * shape, so `.lib` chunks get the same `?retry=N` recovery as component chunks.
 *
 * Attached as a property on `loadable` (rather than exported under its own name) because
 * `@loadable/babel-plugin` matches `.lib` callsites syntactically: `<ident>.lib(...)` where
 * `<ident>` is named `loadable` (see its `isValidIdentifier`). A standalone
 * `loadableLib(...)` callsite is invisible to the plugin, so its `() => import(...)` is
 * left as a plain thunk — and `@loadable/component`'s
 * `invariant(!props.__chunkExtractor || ctor.requireSync, 'SSR requires @loadable/babel-plugin')`
 * runs *before* the `ssr: false` early-return, so an untransformed `.lib` throws during SSR
 * on any page that renders it (e.g. `LottiePlayer`, which renders its lib unconditionally).
 * Keep the callsite shape as `loadable.lib(...)`;
 * test/build-contracts/loadableLibTransform.contract.spec.ts pins this.
 */
const lib = <TModule>(importFn: () => Promise<TModule>, options?: LoadableOptions): LoadableLibrary<TModule> => {
  const wrappedLoader = wrapLoaderArg(importFn) as () => Promise<TModule>;
  return loadableOriginal.lib(wrappedLoader as () => Promise<TModule>, options);
};

loadable.lib = lib;

// Internal exports for unit testing only.
/** Test hook: forget preloaded chunks and re-arm the one-time `languageChanged` subscription. */
const resetNamespacePreloadState = (): void => {
  preloadedNamespaces.clear();
  languageChangeListenerInstalled = false;
};

export const __testing__ = { isChunkLoadError, withChunkRetry, wrapLoaderArg, withQueryParam, withNamespacePreload, resetNamespacePreloadState };
